import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorMessage } from '@/components/ui/errormessage'
import { InlineHelpTooltip } from '@/components/ui/inline-help-tooltip'
import { Input } from '@/components/ui/input'
import { Label, RequiredIndicator } from '@/components/ui/label'
import { Picker } from '@/components/ui/picker'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { KBD_SUBMIT_ATTR, kbdFieldProps } from '@/hooks/formKeyboardNav'
import { useFormKeyboardNav } from '@/hooks/useFormKeyboardNav'
import { type ReactNode, useState } from 'react'
import type { ZodSchema } from 'zod'
import { ZodError } from 'zod'
import { Separator } from '../ui/separator'
import { CustomDurationInput } from './CustomDurationInput'
import { SearchableSelect } from './SearchableSelect'

// Field configuration types
export interface SelectOption {
	value: string
	label: string
}

export interface FormField {
	key: string
	label: string
	type: 'text' | 'email' | 'password' | 'textarea' | 'select' | 'combobox' | 'duration'
	placeholder?: string
	customInputPlaceholder?: string
	required?: boolean
	/** Static option list, or a resolver computed from the current form values (for dependent fields). */
	options?: SelectOption[] | ((values: Record<string, unknown>) => SelectOption[])
	disabled?: boolean
	disabledReason?: string // Tooltip message shown when field is disabled
	help?: {
		label: string
		content: ReactNode
		dataTestId: string
	}
	readOnly?: boolean
	normalize?: boolean
	durationUnit?: string // Optional unit suffix, e.g. "days"
}

export interface GenericFormModalProps<T = Record<string, unknown>> {
	isOpen: boolean
	onClose: () => void
	onSuccess?: () => void
	mode: 'create' | 'edit'
	title?: string
	fields: FormField[]
	initialValues?: Partial<T>
	onSubmit: (values: T) => Promise<void> | void
	submitLabel?: string
	cancelLabel?: string
	isLoading?: boolean
	maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
	validationSchema?: ZodSchema<unknown>
	/** Callback to get warnings (non-blocking) for field values. Returns a map of field key to warning message */
	onFieldWarning?: (key: string, value: string, allValues: Partial<T>) => string | undefined
	/**
	 * Derive other field values when a field changes (e.g. reset role when project changes).
	 * Return a partial patch to merge into the form values, or nothing to leave them untouched.
	 */
	onFieldChange?: (key: string, value: string, allValues: Partial<T>) => Partial<T> | undefined
	testIds?: {
		root?: string
		title?: string
		submit?: string
		cancel?: string
		field?: Partial<Record<string, string>>
	}
}

const MAX_WIDTH_CLASS: Record<NonNullable<GenericFormModalProps['maxWidth']>, string> = {
	sm: 'max-w-sm',
	md: 'max-w-md',
	lg: 'max-w-lg',
	xl: 'max-w-xl',
	'2xl': 'max-w-2xl',
	'3xl': 'max-w-3xl',
	'4xl': 'max-w-4xl',
}

function getInitialFormValues<T extends Record<string, unknown>>(
	fields: FormField[],
	initialValues: Partial<T>,
): Partial<T> {
	const defaultValues: Record<string, unknown> = {}
	for (const field of fields) {
		defaultValues[field.key] = initialValues[field.key] || ''
	}
	return defaultValues as Partial<T>
}

function getFormSeedKey<T extends Record<string, unknown>>(
	isOpen: boolean,
	fields: FormField[],
	initialValues: Partial<T>,
): string {
	return JSON.stringify([isOpen, fields.map(field => [field.key, initialValues[field.key]])], (_key, value) =>
		typeof value === 'bigint' ? value.toString() : value,
	)
}

export function GenericFormModal<T extends Record<string, unknown>>(props: GenericFormModalProps<T>) {
	const seedKey = getFormSeedKey(props.isOpen, props.fields, props.initialValues ?? {})
	return <GenericFormModalContent key={seedKey} {...props} />
}

function GenericFormModalContent<T extends Record<string, unknown>>({
	isOpen,
	onClose,
	onSuccess,
	mode,
	title,
	fields,
	initialValues = {},
	onSubmit,
	submitLabel,
	cancelLabel = 'Cancel',
	isLoading = false,
	maxWidth = 'md',
	validationSchema,
	onFieldWarning,
	onFieldChange,
	testIds,
}: GenericFormModalProps<T>) {
	const [formValues, setFormValues] = useState<Partial<T>>(() => getInitialFormValues(fields, initialValues))
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [warnings, setWarnings] = useState<Record<string, string>>({})

	const handleFieldChange = (key: string, value: string) => {
		const sanitizedValue = value
		let updatedFormValues = { ...formValues, [key]: sanitizedValue }

		// Let the parent derive dependent fields (e.g. reset role when project changes)
		if (onFieldChange) {
			const patch = onFieldChange(key, sanitizedValue, updatedFormValues as Partial<T>)
			if (patch) {
				updatedFormValues = { ...updatedFormValues, ...patch }
			}
		}

		setFormValues(updatedFormValues as Partial<T>)

		// Validate only the field being changed
		if (validationSchema) {
			const result = validationSchema.safeParse(updatedFormValues)
			if (result.success) {
				// Clear error for this field if valid
				if (errors[key]) {
					setErrors(prev => {
						const updated = { ...prev }
						delete updated[key]
						return updated
					})
				}
			} else {
				// Find error for this specific field only
				const fieldError = result.error.issues.find(issue => issue.path[0] === key)
				if (fieldError) {
					if (key === 'expiry_period' && sanitizedValue === 'custom-') {
						if (errors[key]) {
							setErrors(prev => {
								const updated = { ...prev }
								delete updated[key]
								return updated
							})
						}
					} else {
						setErrors(prev => ({ ...prev, [key]: fieldError.message }))
					}
				} else if (errors[key]) {
					// Clear error if this field is now valid
					setErrors(prev => {
						const updated = { ...prev }
						delete updated[key]
						return updated
					})
				}
			}
		} else {
			// Fallback validation for the specific field only
			const field = fields.find(f => f.key === key)
			if (field) {
				// Check required validation
				if (field.required && (!sanitizedValue || sanitizedValue.trim() === '')) {
					setErrors(prev => ({ ...prev, [key]: `${field.label} is required` }))
					return
				}

				// Check email validation
				if (field.type === 'email' && sanitizedValue) {
					const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
					if (!emailRegex.test(sanitizedValue)) {
						setErrors(prev => ({ ...prev, [key]: 'Please enter a valid email address' }))
						return
					}
				}

				// Check normalize validation
				if (field.normalize && sanitizedValue) {
					if (/[^a-z0-9_]/.test(sanitizedValue)) {
						setErrors(prev => ({
							...prev,
							[key]: `${field.label} can only contain lowercase letters, numbers, and underscores`,
						}))
						return
					}
				}

				// Clear error if validation passes
				if (errors[key]) {
					setErrors(prev => {
						const updated = { ...prev }
						delete updated[key]
						return updated
					})
				}
			}
		}

		// Check for warnings (non-blocking)
		if (onFieldWarning) {
			const warning = onFieldWarning(key, sanitizedValue, updatedFormValues as Partial<T>)
			if (warning) {
				setWarnings(prev => ({ ...prev, [key]: warning }))
			} else {
				setWarnings(prev => {
					if (!prev[key]) {
						return prev
					}
					const updated = { ...prev }
					delete updated[key]
					return updated
				})
			}
		}
	}

	// Validate form before submission
	const validateForm = (): boolean => {
		if (validationSchema) {
			try {
				validationSchema.parse(formValues)
				setErrors({})
				return true
			} catch (err) {
				if (err instanceof ZodError) {
					const fieldErrors: Record<string, string> = {}
					for (const e of err.issues) {
						const field = e.path[0] as string
						// First error per field wins — Zod returns all failing checks
						// (e.g. min + regex both fail on empty), so the first one is most relevant
						if (field && !fieldErrors[field]) {
							fieldErrors[field] = e.message
						}
					}
					setErrors(fieldErrors)
					const hiddenErrors = Object.entries(fieldErrors).filter(([key]) => !fields.some(f => f.key === key))
					for (const [key, message] of hiddenErrors) {
						console.error(`${key}: ${message}`)
					}
				}
				return false
			}
		}

		// Fallback: default required + email validation
		const newErrors: Record<string, string> = {}
		for (const field of fields) {
			if (
				field.required &&
				(!formValues[field.key] ||
					(typeof formValues[field.key] === 'string' && (formValues[field.key] as string).trim() === ''))
			) {
				newErrors[field.key] = `${field.label} is required`
			}
			if (field.type === 'email' && formValues[field.key]) {
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
				if (!emailRegex.test(String(formValues[field.key] ?? ''))) {
					newErrors[field.key] = 'Please enter a valid email address'
				}
			}
			if (field.normalize && formValues[field.key]) {
				if (/[^a-z0-9_]/.test(String(formValues[field.key] ?? ''))) {
					newErrors[field.key] = `${field.label} can only contain lowercase letters, numbers, and underscores`
				}
			}
		}
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = async () => {
		if (!validateForm()) {
			return
		}
		try {
			await onSubmit(formValues as T)
			onSuccess?.()
			onClose()
		} catch (error) {
			console.error('Form submission error:', error)
		}
	}

	const handleFieldBlur = (key: string) => {
		handleFieldChange(key, String(formValues[key] ?? ''))
	}

	const getFieldTestId = (key: string) => testIds?.field?.[key]

	const renderField = (field: FormField) => {
		const fieldTestId = getFieldTestId(field.key)
		const hasError = !!errors[field.key]
		const resolvedOptions = typeof field.options === 'function' ? field.options(formValues) : (field.options ?? [])

		switch (field.type) {
			case 'textarea':
				return (
					<Textarea
						id={field.key}
						data-testid={fieldTestId}
						value={String(formValues[field.key] ?? '')}
						onChange={e => handleFieldChange(field.key, e.target.value)}
						onBlur={() => handleFieldBlur(field.key)}
						placeholder={field.placeholder}
						disabled={field.disabled}
						readOnly={field.readOnly}
						className='min-h-20 resize-none pt-2 text-body bg-card'
						aria-invalid={hasError ? true : undefined}
						{...kbdFieldProps(field.type)}
					/>
				)
			case 'select':
				return (
					<SearchableSelect
						value={String(formValues[field.key] ?? '')}
						options={resolvedOptions}
						onChange={val => handleFieldChange(field.key, val)}
						placeholder={field.placeholder}
						disabled={field.disabled}
						data-testid={fieldTestId}
						{...kbdFieldProps(field.type)}
					/>
				)
			case 'combobox':
				return (
					<Picker
						mode='single'
						id={field.key}
						value={String(formValues[field.key] ?? '')}
						onValueChange={val => handleFieldChange(field.key, val)}
						options={resolvedOptions}
						placeholder={field.placeholder}
						disabled={field.disabled}
						data-testid={fieldTestId}
						aria-invalid={hasError ? true : undefined}
						{...kbdFieldProps(field.type)}
					/>
				)
			case 'duration':
				return (
					<CustomDurationInput
						value={String(formValues[field.key] ?? '')}
						onChange={val => handleFieldChange(field.key, val)}
						options={resolvedOptions}
						placeholder={field.placeholder}
						customInputPlaceholder={field.customInputPlaceholder}
						disabled={field.disabled}
						unit={field.durationUnit}
						data-testid={fieldTestId}
						aria-invalid={hasError ? true : undefined}
					/>
				)
			default:
				return (
					<Input
						id={field.key}
						type={field.type}
						data-testid={fieldTestId}
						value={String(formValues[field.key] ?? '')}
						onChange={e => handleFieldChange(field.key, e.target.value)}
						onBlur={() => handleFieldBlur(field.key)}
						normalize={field.normalize}
						placeholder={field.placeholder}
						disabled={field.disabled}
						readOnly={field.readOnly}
						className='h-control text-body bg-card'
						aria-invalid={hasError ? true : undefined}
						{...kbdFieldProps(field.type)}
					/>
				)
		}
	}

	const defaultTitle = mode === 'create' ? 'Create New Item' : 'Edit Item'
	const defaultSubmitLabel = mode === 'create' ? 'Create' : 'Save Changes'

	// Wrap field with tooltip if disabled and has disabledReason
	const renderFieldWithTooltip = (field: FormField) => {
		const fieldElement = renderField(field)

		if (field.disabled && field.disabledReason) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className='cursor-not-allowed **:cursor-not-allowed!'>{fieldElement}</div>
					</TooltipTrigger>
					<TooltipContent>{field.disabledReason}</TooltipContent>
				</Tooltip>
			)
		}

		return fieldElement
	}

	// Form content (reusable for both dialog and inline modes)
	const formContent = (
		<div className='space-y-form-field'>
			{fields.map(field => {
				const fieldTestId = getFieldTestId(field.key)
				return (
					<div key={field.key}>
						<div className='flex flex-col gap-2'>
							<div className='flex items-center gap-1'>
								<Label
									htmlFor={field.key}
									data-testid={fieldTestId ? `${fieldTestId}-label` : undefined}
									className='text-control text-muted-foreground'>
									{field.label}
									{field.required && <> {RequiredIndicator}</>}
								</Label>
								{field.help && (
									<InlineHelpTooltip
										label={field.help.label}
										content={field.help.content}
										dataTestId={field.help.dataTestId}
									/>
								)}
							</div>
							{renderFieldWithTooltip(field)}
						</div>
						{errors[field.key] && (
							<ErrorMessage
								message={errors[field.key]}
								data-testid={fieldTestId ? `${fieldTestId}-error` : undefined}
							/>
						)}
						{!errors[field.key] && warnings[field.key] && (
							<p
								data-testid={fieldTestId ? `${fieldTestId}-warning` : undefined}
								className='text-control text-warning mt-1'>
								{warnings[field.key]}
							</p>
						)}
					</div>
				)
			})}
		</div>
	)

	const maxWidthClass = MAX_WIDTH_CLASS[maxWidth]
	const handleFormKeyboardNav = useFormKeyboardNav()

	const formFooter = (
		<div className='shrink-0 p-4'>
			<Separator />
			<div className='flex justify-end gap-2 pt-4'>
				<Button variant='outline' onClick={onClose} disabled={isLoading} data-testid={testIds?.cancel}>
					{cancelLabel}
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={isLoading}
					data-testid={testIds?.submit}
					{...{ [KBD_SUBMIT_ATTR]: '' }}>
					{isLoading ? 'Loading...' : submitLabel || defaultSubmitLabel}
				</Button>
			</div>
		</div>
	)

	// Conditional rendering: inline popover for edit mode (FirmwarePopoverContent-style), dialog for create mode
	return mode === 'edit' ? (
		<div
			data-testid={testIds?.root}
			className={`relative w-full ${maxWidthClass} min-w-sm max-h-100 bg-background rounded-md flex flex-col overflow-hidden`}
			onKeyDown={handleFormKeyboardNav}>
			<div className='flex-1 overflow-y-auto min-h-0'>
				<div className='p-4'>
					{title ? (
						<h3 data-testid={testIds?.title} className='text-form-modal-title font-bold mb-3'>
							{title}
						</h3>
					) : null}
					{formContent}
				</div>
			</div>
			{formFooter}
		</div>
	) : (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				data-testid={testIds?.root}
				className={`w-full ${maxWidthClass} max-h-[90vh] flex flex-col p-0 [&>button]:hidden overflow-hidden bg-background rounded-md shadow-levitating`}>
				<DialogHeader className='shrink-0 px-6 pt-6 pb-2'>
					<DialogTitle data-testid={testIds?.title} className='text-form-modal-title font-bold'>
						{title || defaultTitle}
					</DialogTitle>
				</DialogHeader>

				<div className='flex-1 overflow-y-auto min-h-0 px-6 py-4'>
					<div className='overflow-visible'>{formContent}</div>
				</div>

				{formFooter}
			</DialogContent>
		</Dialog>
	)
}
